/**
 * `<caos-overflow-menu>` — overflow of secondary actions behind a single trigger.
 * (Catalog: Actions › OverflowMenu — overflow of secondary actions behind a single trigger.)
 *
 * A "⋯" trigger button opens a floating `role="menu"` of `role="menuitem"`s. The
 * trigger is a native `<button>` (styled as a token-driven ghost icon button) so the
 * `aria-haspopup="menu"` / `aria-expanded` state and keyboard focus live on the real
 * control. Items come from the `items` property (`{ value, label, disabled?, danger?,
 * separator? }[]`) or declarative `<button>` children. Full keyboard model: Enter /
 * Space / ArrowDown open (focus first item) and ArrowUp opens on the last; within the
 * menu Arrow Up/Down + Home/End roam, Enter/Space activate, Esc closes and returns
 * focus to the trigger; a click outside closes. The popover is the one floating
 * surface, so it carries `--caos-shadow-float`. A bubbling, composed `caos-menu-select`
 * (detail `{ value }`) fires on activation.
 *
 * Menu: portaled to `document.body` (its own shadow host, `position: fixed`, anchored to the
 * trigger's live screen rect) so NOTHING around it can clip it — the same technique as
 * `<caos-app-launcher>`, `<caos-nav-gadget>` and `<caos-object-caret>`. This menu is placed in
 * exactly the boxes that clip: a table row (`<caos-data-table>` scrolls inside its own
 * `overflow-x` container) and an action bar. An absolutely-positioned menu is cut off at the
 * first such edge, which is what happened on the Component Library page — the menu opened and was
 * half-hidden behind the block below it (CAOS-587). All four floating panels now escape the same
 * way; the missing systemic layering scale that made each of them invent an answer is CAOS-586.
 *
 * Layering: the menu asks for a NAMED layer, `--caos-layer-dropdown`, rather than writing a
 * stacking number into this stylesheet. The layering axis is decided (CAOS-586): a component says
 * which layer it belongs to and the platform resolves it against where the component actually
 * sits, because the same menu opened on a page belongs below a dialog and opened inside that
 * dialog belongs above it — a fixed number cannot be right in both places. `dropdown` is the layer
 * named for exactly this: anything a control opens in place and anchors to itself. Until the token
 * ships, the `var()` falls back to the value the three sibling panels already carry, so this
 * behaves identically today and follows the scale with no further change here.
 *
 * BREAKING CHANGE TO THE PUBLIC STYLING CONTRACT — `::part(menu)` and `::part(item)` ARE GONE.
 * This component used to expose three styling seams; it now exposes ONE. `::part()` reaches into a
 * component's own shadow tree and no further, and the menu now lives in a separate tree at the end
 * of the document, so its parts cannot be addressed from outside by anyone. Nothing in the repo
 * consumed them when they were removed, and the other three floating panels never offered any —
 * but this is a REMOVAL from a documented contract, not an omission, and a design system that
 * needs to restyle this menu per org must be given a TOKEN to read instead (custom properties do
 * inherit into the portaled tree). Recorded in `dev-docs/.../design-system.md` under the styling
 * seams ladder. The price is paid deliberately: nothing can clip the menu.
 *
 * Styling seams: `::part(trigger)` — and only that.
 */
import { CaosElement, canOpenFloatingPanel, define, makeSheet, RESET } from '../base-element.js';
import { panelFocus, type PanelFocus } from '../focus.js';
import { anchorPanel } from '../anchored-panel.js';
import { makeIcon } from './icons.js';

export interface CaosMenuItem {
  value: string;
  label: string;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
}

/** Distance between the trigger and the menu it opens, and the margin kept off the viewport edge. */
const GAP = 4;
const EDGE = 8;

const sheet = makeSheet(`
  :host { display: inline-block; position: relative; }

  .trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    appearance: none;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--caos-radius);
    color: var(--caos-color-text-muted);
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  }
  .trigger:hover { background: var(--caos-color-surface-2); color: var(--caos-color-text); }
  .trigger[aria-expanded="true"] {
    background: var(--caos-color-surface-2);
    border-color: var(--caos-color-border);
    color: var(--caos-color-text);
  }
  .trigger:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-color: var(--caos-color-accent); }
  .trigger svg { display: block; width: var(--caos-size-xs-height); height: var(--caos-size-xs-height); }
`);

/**
 * Shadow-root stylesheet for the portaled menu (body-hosted, so nothing can clip it).
 *
 * This root is created by hand rather than by the base class, so it carries only what it is handed.
 * It is handed the shared RESET first and this sheet second (see `ensurePortal`) — the reset is
 * where the box model and the `[hidden]` guarantee live, and a root that reproduces one line of it
 * locally is a root that silently misses the rest. That is why there is no box-sizing rule here.
 */
const panelSheet = makeSheet(`
  .menu {
    position: fixed;
    /* A NAMED layer, not a number - see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown, 500);
    min-width: 200px;
    max-width: 320px;
    max-height: 70vh;
    overflow-y: auto;
    padding: var(--caos-space-1);
    margin: 0;
    list-style: none;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    box-shadow: var(--caos-shadow-float);
    font-family: var(--caos-font);
  }
  .menu[hidden] { display: none; }

  .item {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    width: 100%;
    padding: var(--caos-space-2) var(--caos-space-3);
    appearance: none;
    background: transparent;
    border: none;
    border-radius: var(--caos-radius-sm);
    font-family: inherit;
    font-size: 13px;
    line-height: 1.3;
    text-align: left;
    color: var(--caos-color-text);
    cursor: pointer;
    white-space: nowrap;
  }
  .item:hover:not([aria-disabled="true"]),
  .item:focus-visible {
    outline: none;
    background: var(--caos-color-surface-2);
  }
  .item[data-danger="true"] { color: var(--caos-color-danger); }
  .item[data-danger="true"]:hover:not([aria-disabled="true"]),
  .item[data-danger="true"]:focus-visible { background: var(--caos-color-danger-bg); color: var(--caos-color-danger-text); }
  .item[aria-disabled="true"] { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }

  .sep { height: 1px; margin: var(--caos-space-1) 0; background: var(--caos-color-border); }
`);

export class CaosOverflowMenu extends CaosElement {
  private trigger: HTMLButtonElement;
  private _items: CaosMenuItem[] | null = null;
  private itemEls: HTMLButtonElement[] = [];
  private isOpen = false;

  // The menu lives in its OWN shadow host appended to <body>, so no ancestor's overflow, stacking
  // context or transform can clip it or paint over it.
  private portalHost: HTMLDivElement | null = null;
  /** CAOS-651 — focus into the open menu, kept there, and handed back on close. */
  private panelFocus: PanelFocus | null = null;
  private portalRoot: ShadowRoot | null = null;
  private menu!: HTMLUListElement;

  private onDocPointer = (e: Event): void => {
    const path = e.composedPath();
    if (path.includes(this)) return;
    if (this.portalHost && path.includes(this.portalHost)) return;
    this.close(false);
  };
  private readonly reposition = (): void => {
    if (!this.isOpen) return;
    // Right-aligned to the trigger by default (a row action sits at the right edge of its row);
    // `align="start"` lines it up with the trigger's left edge instead. Either way it opens above
    // the trigger when there is no room below, and is kept inside the visible viewport (CAOS-659).
    const align = this.getAttribute('align') === 'start' ? 'start' : 'end';
    anchorPanel(this.menu, this.trigger, { side: 'bottom', align, gap: GAP, margin: EDGE });
  };

  constructor() {
    super(sheet);

    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'trigger';
    this.trigger.setAttribute('part', 'trigger');
    this.trigger.setAttribute('aria-haspopup', 'menu');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', 'More actions');
    this.trigger.append(makeIcon('more'));
    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (e) => this.onTriggerKeydown(e));

    this.root.append(this.trigger);
  }

  connectedCallback(): void {
    this.ensurePortal();
    if (this._items === null) this.readLightDom();
    this.trigger.setAttribute('aria-label', this.triggerLabel());
    this.menu.setAttribute('aria-label', this.triggerLabel());
    this.render();
  }

  /** What this menu is called — the host's `label`, or the generic name for an unlabelled one. */
  private triggerLabel(): string {
    const label = this.getAttribute('label');
    return label && label.trim() ? label.trim() : 'More actions';
  }

  disconnectedCallback(): void {
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.portalHost?.remove();
    this.portalHost = null;
    this.portalRoot = null;
    this.panelFocus = null;
    this.isOpen = false;
    // The trigger is the one element that survives a disconnect — reused as-is on reconnect,
    // never recreated — so its `aria-expanded` is the one piece of open/closed state that
    // outlives the round trip unless it is reset here too. Without this line, a control torn
    // down while open (the ordinary shape of a list re-render) comes back still announcing
    // "expanded" to a screen reader over a menu that this callback just discarded (CAOS-633).
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  /** The menu items (data in). */
  get items(): CaosMenuItem[] {
    return this._items ?? [];
  }
  set items(list: CaosMenuItem[]) {
    this._items = list;
    this.render();
  }

  get open(): boolean {
    return this.isOpen;
  }

  /** Build the body-hosted portal shadow root that carries the menu. */
  private ensurePortal(): void {
    if (this.portalHost) return;
    this.portalHost = document.createElement('div');
    this.portalHost.className = 'caos-overflow-menu-portal';
    this.portalRoot = this.portalHost.attachShadow({ mode: 'open' });
    // RESET FIRST: this root is outside the base class, so it only carries what it is given.
    this.portalRoot.adoptedStyleSheets = [RESET, panelSheet];

    this.menu = document.createElement('ul');
    this.menu.className = 'menu';
    this.menu.setAttribute('role', 'menu');
    // The menu is in its own tree at the end of <body>, so it cannot be named by a reference back
    // to the trigger — an id in one shadow tree is not addressable from another. It carries the
    // trigger's own label instead, so it is at least announced as the thing that opened it.
    this.menu.setAttribute('aria-label', this.triggerLabel());
    this.menu.hidden = true;
    this.menu.addEventListener('keydown', (e) => this.onMenuKeydown(e));

    this.portalRoot.append(this.menu);
    document.body.append(this.portalHost);
  }

  private readLightDom(): void {
    const nodes = Array.from(this.children).filter((c) => c.tagName === 'BUTTON');
    if (nodes.length) {
      this._items = nodes.map((n) => ({
        value: (n as HTMLButtonElement).value || (n.textContent ?? '').trim(),
        label: (n.textContent ?? '').trim(),
        disabled: n.hasAttribute('disabled'),
        danger: (n as HTMLElement).dataset.danger === 'true',
      }));
      // hide the declarative source nodes; the portaled menu is authoritative
      nodes.forEach((n) => ((n as HTMLElement).hidden = true));
    }
  }

  private render(): void {
    // NEVER build the portal for an element that is not on the page. `items` is a property, so a
    // list surface that builds its rows off-DOM and discards some would otherwise leave one
    // orphaned <div> on <body> per discarded row, with no disconnectedCallback to clean it up.
    // The portal is built on connect and on open; until then the items are simply held.
    if (!this.portalHost) {
      if (!this.isConnected) return;
      this.ensurePortal();
    }
    const items = this._items ?? [];
    this.menu.replaceChildren();
    this.itemEls = [];
    items.forEach((it) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'none');
      if (it.separator) {
        const sep = document.createElement('div');
        sep.className = 'sep';
        li.append(sep);
        this.menu.append(li);
        return;
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'item';
      btn.setAttribute('role', 'menuitem');
      btn.tabIndex = -1;
      btn.dataset.value = it.value;
      btn.textContent = it.label;
      if (it.danger) btn.dataset.danger = 'true';
      if (it.disabled) btn.setAttribute('aria-disabled', 'true');
      btn.addEventListener('click', () => this.activate(btn));
      li.append(btn);
      this.menu.append(li);
      this.itemEls.push(btn);
    });
  }

  private toggle(): void {
    this.isOpen ? this.close(true) : this.openMenu(0);
  }

  private openMenu(focusIndex: number): void {
    if (this.isOpen) return;
    // The OPEN path is the other door onto the same floor as `render()`'s connect/data-in check —
    // a control that is not on the document must not get a panel on `document.body` (CAOS-632).
    if (!canOpenFloatingPanel(this)) return;
    // Opening is the other moment the portal is allowed to exist. Render again in case the items
    // were set while this element was off the page, when they were held rather than drawn.
    this.ensurePortal();
    this.render();
    this.isOpen = true;
    this.menu.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.reposition();
    document.addEventListener('pointerdown', this.onDocPointer, true);
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
    const enabled = this.enabledItems();
    const target = focusIndex < 0 ? enabled[enabled.length - 1] : enabled[Math.max(0, focusIndex)];
    // The items are out of the tab order (the arrow keys move between them), so they are what Tab
    // cycles through as well.
    this.panelFocus = panelFocus(this.menu, () => this.enabledItems());
    this.panelFocus.open({ opener: this.trigger, initial: target ?? null });
  }

  private close(returnFocus: boolean): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.menu.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.panelFocus?.close(returnFocus);
    this.panelFocus = null;
  }

  private activate(btn: HTMLButtonElement): void {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    const value = btn.dataset.value ?? '';
    this.dispatchEvent(new CustomEvent('caos-menu-select', { bubbles: true, composed: true, detail: { value } }));
    this.close(true);
  }

  private enabledItems(): HTMLButtonElement[] {
    return this.itemEls.filter((b) => b.getAttribute('aria-disabled') !== 'true');
  }

  private onTriggerKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.openMenu(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.openMenu(-1);
    }
  }

  private onMenuKeydown(e: KeyboardEvent): void {
    const enabled = this.enabledItems();
    if (!enabled.length && e.key !== 'Escape' && e.key !== 'Tab') return;
    // The items live in the portal's shadow tree, so that is where the focused one is found.
    const active = this.portalRoot?.activeElement as HTMLButtonElement | null;
    const idx = active ? enabled.indexOf(active) : -1;
    const last = enabled.length - 1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        enabled[idx >= last ? 0 : idx + 1]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        enabled[idx <= 0 ? last : idx - 1]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        enabled[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        enabled[last]?.focus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (active) this.activate(active);
        break;
      case 'Escape':
        e.preventDefault();
        this.close(true);
        break;
      case 'Tab':
        // CAOS-651 — Tab stays inside the open menu, cycling its items, as it does in every panel on
        // the top layer. Escape is the way out, and it hands focus back to the trigger.
        this.panelFocus?.trap(e);
        break;
    }
  }
}

define('caos-overflow-menu', CaosOverflowMenu);

declare global {
  interface HTMLElementTagNameMap {
    'caos-overflow-menu': CaosOverflowMenu;
  }
}
